import { ALL, ME, RECRUTEUR } from "Components/Management/ManagementButtons";
import { store } from "Redux/store";
import DOMPurify from "dompurify";
import { DateTime } from "luxon";

/**
 * Fonction permettant de récupérer un cookie par son nom
 * @param {string} name - nom du cookie
 * @returns {string} - valeur du cookie
 */
export function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

/**
 * Fonction permettant de convertir une date comme Tue Aug 30 2022 00:00:00 GMT+0200
 * @param {Date} date
 * @returns {String} Date DD/MM/YYYY
 */
export const convert = (str) => {
  //! Si la date est invalide, on retourne une chaîne vide
  if (isNaN(Date.parse(str))) return "";

  var date = new Date(str),
    mnth = ("0" + (date.getMonth() + 1)).slice(-2),
    day = ("0" + date.getDate()).slice(-2);
  return [day, mnth, date.getFullYear()].join("/");
};
/**
 * Fonction permettant d'afficher une date sous forme de texte en français
 * @param {Date} date
 * @returns {String} Date en français (ex: lundi 8 janvier 2022) | vide si date invalide
 */
export const formatDateFrenchLocale = (date) => {
  if (isNaN(Date.parse(date))) {
    console.log("The date '" + date + "' is not a valid date");
    return "";
  }
  const jsDate = new Date(date);
  const actualDate = DateTime.fromJSDate(jsDate, { locale: "fr" });
  return (
    actualDate.weekdayLong +
    " " +
    actualDate.day +
    " " +
    actualDate.monthLong +
    " " +
    actualDate.year
  );
};

/**
 *
 * @param {Date} date
 * @returns {String} Date en nomenclature DD.MM.YYYY | vide si date invalide
 */
export const formatDate = (date, separator = "/") => {
  if (isNaN(Date.parse(date))) {
    // console.log("The date '" + date + "' is not a valid date");
    return "";
  }

  const actualDate = new Date(date);
  const day = actualDate.getDate().toLocaleString("fr-FR", {
    minimumIntegerDigits: 2,
  });
  const month = (actualDate.getUTCMonth() + 1).toLocaleString("fr-FR", {
    minimumIntegerDigits: 2,
  });
  const year = actualDate.getUTCFullYear();
  return day + separator + month + separator + year;
};

/**
 *
 * @param {Date} date
 * @returns {String} Date en nomenclature MM.YYYY | vide si la date n'est pas déjà sur ce format
 */
export const formatDateWihoutDays = (date) => {
  if (date !== null) {
    if (date.length !== 7) {
      const actualDate = new Date(date);

      const month = (actualDate.getUTCMonth() + 1).toLocaleString("fr-FR", {
        minimumIntegerDigits: 2,
      });
      const year = actualDate.getUTCFullYear();
      return `${month}/${year}`;
    } else return date;
  }
};

/**
 * Fonction permettant de formater une date qui n'a pas de jour et de changer l'ordre des mois et de l'année
 * (en prenant en compte l'offset de la timezone)
 * @param {Date} date format month/year
 * @returns {String} la date formatée en string : YYYY-MM-DD |
 */
export const changeOrderDate = (date) => {
  const dateSplited = date.split("/");
  const month = dateSplited[0];
  const year = dateSplited[1];
  return `${year}-${month}-01`;
};

/**
 * Fonction permettant de formater une date et de changer l'ordre des mois et de l'année et des jours
 * (en prenant en compte l'offset de la timezone)
 * @param {Date} date format month/year
 * @returns {String} la date formatée en string : YYYY-MM-DD |
 */
export const changeOrderDateWithDay = (date) => {
  const dateSplited = date.split("/");
  const day = dateSplited[0];
  const month = dateSplited[1];
  const year = dateSplited[2];
  return `${year}-${month}-${day}`;
};
/**
 * Fonction permettant de formater une date en français, en string
 * (en prenant en compte l'offset de la timezone)
 * @param {Date} date
 * @returns {String} la date formatée en string : YYYY-MM-DD | vide si date invalide
 */
export const formatDateDatabase = (date) => {
  if (isNaN(Date.parse(date))) {
    console.log("The date '" + date + "' is not a valid date");
    return "";
  }

  let newDate = new Date(date - date.getTimezoneOffset() * 60000)
    .toISOString()
    .split("T")[0];
  return newDate;
};

/**
 * Fonction permettant de formater une date en français, en string
 * (en prenant en compte l'offset de la timezone) avec l'heure et les minutes
 * @param {Date} date
 * @returns {String} la date formatée en string : DD/MM/YYY à HHhmm | vide si date invalide
 */
export const formatDateHourFrenchLocale = (date) => {
  if (isNaN(Date.parse(date))) {
    console.log("The date '" + date + "' is not a valid date");
    return "";
  }

  const actualDate = new Date(date);
  const day = actualDate.getDate().toLocaleString("fr-FR", {
    minimumIntegerDigits: 2,
  });
  const month = (actualDate.getUTCMonth() + 1).toLocaleString("fr-FR", {
    minimumIntegerDigits: 2,
  });
  const year = actualDate.getUTCFullYear();
  let newHour = actualDate.getHours().toLocaleString("fr-FR", {
    minimumIntegerDigits: 2,
  });
  let newMin = actualDate.getMinutes().toLocaleString("fr-FR", {
    minimumIntegerDigits: 2,
  });
  return day + "/" + month + "/" + year + " à " + newHour + "h" + newMin;
};

/**
 * Fonction permettant de formater une date en français, en string
 * (en prenant en compte l'offset de la timezone) avec l'heure et les minutes
 * @param {Date} date
 * @returns {String} HHhmm | vide si date invalide
 */
export const formatHourFrenchLocale = (date) => {
  if (isNaN(Date.parse(date))) {
    return "";
  }

  const actualDate = new Date(date);
  let newHour = actualDate.getHours().toLocaleString("fr-FR", {
    minimumIntegerDigits: 2,
  });
  let newMin = actualDate.getMinutes().toLocaleString("fr-FR", {
    minimumIntegerDigits: 2,
  });
  return newHour + "h" + newMin;
};

/**
 * Fonction permettant de formater une date en français, en string
 * (en prenant en compte l'offset de la timezone)
 * @param {Date} date
 * @returns {String} la date formatée en string à 23h59 : YYYY-MM-DD T HH:MM:SS | null
 */
export const formatDateRegisterEnd = (date) => {
  if (isNaN(Date.parse(date))) {
    console.log("The date '" + date + "' is not a valid date");
    return null;
  }
  date.setHours(23, 59, 0);
  let newDate = new Date(date - date.getTimezoneOffset() * 60000).toISOString();
  return newDate;
};

/**
 * Fonction permettant de formater un mot donné avec une majuscule au début
 * @param {String} word - le mot à formatter
 * @param {boolean} lowerCaseRest - défini si le reste du mot doit être en minuscule ou pas
 * @returns {String} la string, formaté | vide si word n'est pas une string
 */
export const upperCaseFirstLetter = (word, lowerCaseRest = false) => {
  if (typeof word !== "string") {
    console.log("The parameter '" + word + "' is not a string");
    return "";
  }

  if (lowerCaseRest)
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  return word.charAt(0).toUpperCase() + word.slice(1);
};

/**
 * Fonction permettant de générer un ordre aléatoire pour le contenu
 * d'un array donnée en paramètre
 * @param {Array} array
 * @returns  {Array} array avec un ordre aléatoire | vide si array n'est pas un array
 */
export const shuffleArray = (array) => {
  if (!Array.isArray(array)) {
    console.log("The parameter '" + array + "' is not an array");
    return [];
  }

  for (let i = array.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

/**
 * Fonction permettant de trier un array d'Objet par Date.
 * La clé contenant la date est donnée en paramètre
 *
 * @param {Array} tableau
 * @return {Array} array trié par ordre croissant | vide si array n'est pas un array / ou ne contient pas de date / ou ne contient pas de clé
 */
export const sortArrayByDateByKey = (tableau, key) => {
  if (!Array.isArray(tableau)) {
    console.log("The parameter '" + tableau + "' is not an array");
    return [];
  }
  if (!tableau.every((element) => !isNaN(Date.parse(element[key])))) {
    console.log(
      "The parameter '" +
        tableau +
        "' is not an array of object with Date property in the key '" +
        key +
        "'"
    );
    return [];
  }

  function compare(a, b) {
    let aDate = new Date(a[key]);
    let bDate = new Date(b[key]);
    if (aDate < bDate) {
      return -1;
    }
    if (aDate > bDate) {
      return 1;
    }
    return 0;
  }
  return tableau.sort(compare);
};

/**
 * Fonction permettant de récupérer une valeur aléatoire d'un tableau
 * @param {Array} tab
 * @return {Array} étément de l'array
 */
export const getRandomItemNonEqual = (tab, item) => {
  if (!Array.isArray(tab) || tab.length === 0) {
    console.log("The parameter '" + tab + "' is not an array or is empty");
    return null;
  }
  if (tab.includes(item) && tab.length === 1) {
    console.log(
      "The parameter '" +
        tab +
        "' only has one element and is equal to '" +
        item +
        "'"
    );
    return null;
  }

  let randomItem = getRandomItem(tab);
  while (item == randomItem) randomItem = getRandomItem(tab, item);
  return randomItem;
};

/**
 * Fonction permettant de récupérer une valeur aléatoire d'un tableau
 * @param {Array} tab
 * @return {Array} étément de l'array
 */
export const getRandomItem = (tab) => {
  return tab[Math.floor(Math.random() * tab.length)];
};

/**
 * Fonction permettant de récupérer le contrat d'une offre sous le bon format (majuscules, minuscules)
 * @param {String} contrat
 * @return {String} contrat formaté | vide si contrat n'est pas une string ou vide
 */

export const formatContrat = (contrat) => {
  if (typeof contrat !== "string" || !contrat) return "";

  if (
    contrat == "CONTRAT D'APPRENTISSAGE" ||
    contrat == "POST-DOCTORANT" ||
    contrat == "INTERIM" ||
    contrat == "CONTRAT DE PROFESSIONNALISATION"
  )
    return upperCaseFirstLetter(contrat, true);
  else return contrat;
};

/**
 *  Fonction permettant de vérifier si la variable passée en paramètre
 *  peut être parsée en JSON
 *  @param {String} String
 *  @return {Boolean} true si la string peut être parsée en JSON, false sinon
 */ export const isJsonParsable = (string) => {
  try {
    JSON.parse(string);
  } catch (e) {
    return false;
  }
  return true;
};

/**
 * Fonction permettant de vérifier si le tableau passé en paramètre
 * est tout d'abord non null, le cas échéant, si il n'est pas vide
 * @param {Array} array
 */
export const isNotEmptyArray = (array) => {
  return array !== null && array != undefined && array.length > 0;
};

/**
 * Fonction permettant d'ajouter un jour a une date de format MM/YYYY puis de vérifier les dates entre elles
 * est tout d'abord non null, le cas échéant, si il n'est pas vide
 * @param {String} string
 * @return {Boolean} true si le premier paramètre est inférieure au deuxième paramètre
 */
export const addDayToDateAndCompare = (
  startDateToFormated,
  endDateToFormated
) => {
  const monthStart = startDateToFormated.split("/")[0];
  const yearStart = startDateToFormated.split("/")[1];
  const monthEnd = formatDate(endDateToFormated).split("/")[1];
  const yearEnd = formatDate(endDateToFormated).split("/")[2];
  const [newStartDate, newEndDate] = [new Date(), new Date()];
  newStartDate.setMonth(monthStart - 1);
  newStartDate.setFullYear(yearStart);
  newEndDate.setMonth(monthEnd - 1);
  newEndDate.setFullYear(yearEnd);
  return newEndDate >= newStartDate;
};

/**
 * Fonction permettant de calculer le nombre de membres actifs dans un groupe
 * (en plus du créateur)
 * @param {Object} group
 * @param {Array<{id: number, groupMemberStatus: {id: number, label: string}}>} group[].groupMembers
 * @param {Object} group[].createdBy
 * @param {number} group[].createdBy.id
 * @return {string} Contenant le nombre de membres actifs + créateur ainsi que le mot membre au singulier ou pluriel
 */
export const getNumberOfMembersInGroup = (group) => {
  // Si les propriétés ne sont pas valides, on retourne 0

  if (!group || !group.groupMembers || group.groupMembers.length == 0)
    return " " + 0 + " membre";

  // Sinon on récupère le nombre de membres actifs auquel on additionne le créateur
  let numberOfMembers = group.groupMembers.filter(
    (member) => member.groupMemberStatus.label == "ACTIF"
  ).length;

  // Formattage de la réponse : x membre(s)
  return (
    " " +
    numberOfMembers.toString() +
    (numberOfMembers > 1 ? " membres" : " membre")
  );
};

/**
 * Recherche d'un contenu dans la gestion éditoriale de Nexus ou de l'université
 * en fonction de la disponibilité du contenu (global > local > default)
 *
 * Dans le cas 'local', on recherche le contenu de l'université actuelle uniquement
 *
 * @param {Object[]} contentManagement - gestion éditoriale sur un label choisi
 * @param {Date} contentManagement[].updatedAt - date de dernière modification du contenu
 * @param {string} contentManagement[].content - contenu de la gestion éditoriale
 * @param {number} contentManagement[].id - id de la gestion éditoriale
 * @param {string} contentManagement[].editAvailable - disponibilité du contenu (local, global, default)
 * @param {Object} contentManagement[].editedByUniversity - université ayant édité le contenu
 * @param {number} contentManagement[].editedByUniversity.id - id de l'université ayant édité le contenu
 * @param {number} univId - id de l'université (optionnel) : si on veut récupérer le contenu d'une université en particulier (généralement pour la présentation de l'université)
 * @returns {Object} - contenu de la gestion éditoriale
 */
function getContentManagementByAvailability(contentManagement, univId) {
  let content = null;
  if (isNotEmptyArray(contentManagement)) {
    //? On recherche d'abord le contenu global (editAvailable = "global")
    content = contentManagement.find(
      (content) => content?.editAvailable === "global"
    );
    //? Si on ne trouve pas de contenu global, on recherche le contenu local (editAvailable = "local") de l'université (uniquement si univId est renseigné)
    !content &&
      univId &&
      (content = contentManagement.find(
        (content) =>
          content?.editAvailable === "local" &&
          content?.editedByUniversity.id == univId
      ));
    //? Sinon, on recherche le contenu par défaut (editAvailable = "default")
    !content &&
      (content = contentManagement.find(
        (content) => content?.editAvailable === "default"
      ));
  }
  return content;
}

/**
 * Fonction permettant de récupérer le contenu en fonction du label donné, depuis la gestion éditoriale
 *
 * Par priorité, on filtre les contenus globaux, puis locaux, puis par défaut (si aucun des deux n'existe)
 *
 * On effectue cette opération une fois sur la gestion éditoriale de l'université actuelle
 * et si rien n'est trouvé une seconde fois sur la gestion éditoriale du Nexus
 *
 * Les contenus sont recherchés en fonction de leur disponibilité (global > local > default) : voir {@link getContentManagementByAvailability}
 *
 * @param {string} label - label du contenu à récupérer
 * @param {Object[]} nexusManagement - gestion éditoriale de Nexus
 * @param {string} nexusManagement[].label - label du contenu
 * @param {string} nexusManagement[].defaultContent - contenu par défaut
 * @param {number} nexusManagement[].id - id de la gestion éditoriale
 * @param {Object[]} nexusManagement[].contentManagement - différents contenus de la gestion éditoriale choisie
 * @param {Date} nexusManagement[].contentManagement[].updatedAt - date de dernière modification du contenu
 * @param {string} nexusManagement[].contentManagement[].content - contenu
 * @param {number} nexusManagement[].contentManagement[].id - id du contenu
 * @param {string} nexusManagement[].contentManagement[].editAvailable - disponibilité du contenu (local, global, default)
 * @param {Object} nexusManagement[].contentManagement[].editedByUniversity - université ayant édité le contenu
 * @param {number} nexusManagement[].contentManagement[].editedByUniversity.id - id de l'université ayant édité le contenu
 * @param {string} nexusManagement[].contentManagement[].editedByUniversity.slug - slug de l'université ayant édité le contenu
 * @param {string} nexusManagement[].contentManagement[].editedByUniversity.name - nom de l'université ayant édité le contenu
 * @param {Object[]} univManagement - gestion éditoriale de l'université
 * @param {string} univManagement[].label - label du contenu
 * @param {number} univManagement[].id - id de la gestion éditoriale
 * @param {Object[]} univManagement[].contentManagement - différents contenus de la gestion éditoriale choisie
 * @param {Date} univManagement[].contentManagement[].updatedAt - date de dernière modification du contenu
 * @param {string} univManagement[].contentManagement[].content - contenu
 * @param {number} univManagement[].contentManagement[].id - id du contenu
 * @param {string} univManagement[].contentManagement[].editAvailable - disponibilité du contenu (local, global, default)
 * @param {Object} univManagement[].contentManagement[].editedByUniversity - université ayant édité le contenu
 * @param {number} univManagement[].contentManagement[].editedByUniversity.id - id de l'université ayant édité le contenu
 * @param {string} univManagement[].contentManagement[].editedByUniversity.slug - slug de l'université ayant édité le contenu
 * @param {string} univManagement[].contentManagement[].editedByUniversity.name - nom de l'université ayant édité le contenu
 * @param {number} univId - id de l'université (optionnel) : si on veut récupérer le contenu d'une université en particulier
 */
export function findManagementContentByLabel(
  label,
  univManagement,
  nexusManagement,
  specificUniv = null
) {
  let univEditManagement =
    univManagement &&
    univManagement?.find((editManagement) => editManagement?.label === label);
  let nexusEditManagement =
    nexusManagement &&
    nexusManagement?.find((editManagement) => editManagement?.label === label);

  let managementContent = null;
  //? On recherche d'abord pour l'université actuelle
  if (univEditManagement)
    managementContent = getContentManagementByAvailability(
      univEditManagement.contentManagement,
      specificUniv
    );
  //? Si on ne trouve pas de contenu pour l'université actuelle, on recherche depuis le contenu de Nexus
  if (!managementContent && nexusEditManagement)
    managementContent = getContentManagementByAvailability(
      nexusEditManagement.contentManagement
    );

  return (
    managementContent || {
      content:
        nexusEditManagement?.defaultContent ||
        univEditManagement?.defaultContent ||
        "Contenu non disponible",
      updatedAt: null,
    }
  );
}

export const findItemInEditManagementVisibility = (itemToFind, reduxData) => {
  if (reduxData != null && reduxData?.length > 0 && reduxData != undefined) {
    const itemFound = reduxData.filter((item) => item?.label === itemToFind);
    if (itemFound != undefined) {
      return itemFound[0]?.valueManagement.length > 1
        ? itemFound[0].valueManagement.find(
            (item) => item.editAvailable === "local"
          ).value
        : itemFound[0].valueManagement[0].value;
    }
  }
};

/**
 * Fonction permettant de parser une string en base64 en objet JSON (JWT)
 * @param {String} token - token de l'utilisateur en base64
 * @returns {String} - token de l'utilisateur parsé depuis base64
 */
export const parseJwt = (token) => {
  try {
    if (!token || typeof token !== "string") throw new Error("Invalid token");
    var base64Url = token.split(".")[1];
    if (!base64Url) throw new Error("Invalid token");
    var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    const parsed = JSON.parse(jsonPayload);
    return parsed;
  } catch (e) {
    return null;
  }
};

/**
 * Fonction purifiant une string contenant du HTML
 * avec la librairie DOMPurify
 *
 * Si la string ne contient pas de tag HTML,
 * on retourne la string tel quel
 *
 * @param {any} html - 'String' contenant du HTML
 * @returns {String} - String purifié
 */
export const sanitizeHtml = (html) => {
  if (html === null || html === "" || typeof html !== "string") return "";
  if (!html.match(/<(.*)>.?|<(.) \/>/g)) return html;

  return DOMPurify.sanitize(html);
};

/**
 * Fonction permettant de tronquer une string au delà d'un nombre de caractères (n)
 * @param {String} str - String à tronquer
 * @param {Number} n - Taille max de la string
 * @returns - String tronqué pour être affiché
 */
export const truncate = (str, n) => {
  if (str === null || str === "" || !str) return "";
  else str = str.toString();
  //? Si la string contient un tag HTML, on 'purifie' la string <(.)>.?|<(.) />
  if (str.match(/<(.*)>.?|<(.) \/>/g)) str = sanitizeHtml(str);
  return str.length > n ? str.slice(0, n - 1) + "..." : str;
};

/**
 * Fonction permettant de retirer les tags HTML d'une string
 * @param {String} str - String contenant des tags HTML
 */
export const stripHtml = (str) => {
  if (str === null || str === "" || !str) return "";
  else str = str.toString();
  //? Si la string contient un tag HTML, on 'purifie' la string <(.)>.?|<(.) />
  if (str.match(/<(.*)>.?|<(.) \/>/g)) str = sanitizeHtml(str);

  const parser = new DOMParser();

  const parsed = parser.parseFromString(str, "text/html");

  return parsed.body.textContent || "";
};

/**
 * Fonction qui à partir d'un IRI retourne le nom du transmitter
 * @param {String} iri - IRI de transmitter
 */
export const TransmitterIRItoName = (iri) => {
  const id = Number(iri.split("/")[3]);
  const transmittersList = store.getState().secondaryTables.transmittersData;
  const transmitter = transmittersList.find(
    (transmitter) => transmitter.id === id
  );
  return transmitter.name;
};

/**
 * Fonction qui à partir d'une liste de name retourne une liste d'id de transmitter
 * @param {Array} array - Array de string avec des names de transmitter
 */
export const getTransmitterId = (array) => {
  const transmittersList = store.getState().secondaryTables.transmittersData;
  let outputArray = [];
  transmittersList.forEach((baseTransmitter) => [
    array.some((transmitter) => baseTransmitter.name === transmitter) &&
      outputArray.push(baseTransmitter.id),
  ]);
  return outputArray;
};

/**
 * Fonction qui retourne une nouvelle URL de filtrage quand les transmitters changent
 * @param {String} string - route de l'url (ex : offers, news, events)
 * @param {URL} url - Url actuelle
 * @param {Array} array - Liste des noms des nouveaux transmitters
 * @param {Number} number - Id of the User
 */
export const filterByTransmitter = (
  route,
  currentUrl,
  transmitters,
  userId
) => {
  let newUrl = new URL(`${process.env.REACT_APP_BASE_URL_API}/${route}`);
  if (transmitters[0] === RECRUTEUR) {
    newUrl = new URL(
      `${process.env.REACT_APP_BASE_URL_API}/${route}/recruiter`
    );
  }
  newUrl.search = currentUrl.search;
  newUrl.searchParams.delete("transmitter[]");
  newUrl.searchParams.delete("createdByUser[]");
  newUrl.searchParams.delete("createdBy[]");

  if (transmitters[0] === ALL || transmitters[0] === RECRUTEUR) return newUrl;
  if (transmitters[0] === ME) {
    if (route === "offers") {
      newUrl.searchParams.append("createdByUser[]", userId);
    } else {
      newUrl.searchParams.append("createdBy[]", userId);
    }
  } else {
    getTransmitterId(transmitters).forEach((transmitter) => {
      newUrl.searchParams.append("transmitter[]", transmitter);
    });
  }
  return newUrl;
};

export const showSuccess = (message, summary = null, time = null) => {
  const reduxStore = store.getState();
  reduxStore.auth.toast.current.show({
    severity: "success",
    summary: summary || "Succès : ",
    detail: message,
    life: time || 5000,
  });
};

export const showError = (message, summary = null, time = null) => {
  const reduxStore = store.getState();
  reduxStore.auth.toast.current.show({
    severity: "error",
    summary: summary || "Erreur : ",
    detail: message,
    life: time || 5000,
  });
};
